// HandymanDashboard — route-first with a small AI card up top.
// Design: compact AI drop tile + prominent Next Stop + Today's route +
// funnel counts. Mirrors PlumbingDashboard shape, purple accent.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Hammer, Sparkle, MapPin, Clock, ArrowRight, CheckCircle, PaperPlaneTilt,
  Warning, House, Camera,
} from "@phosphor-icons/react";

const STATUS_CHIP = {
  scheduled:  { cls: "bg-slate-200 text-slate-700",    label: "SCHEDULED" },
  en_route:   { cls: "bg-cyan-100 text-cyan-800",      label: "EN ROUTE" },
  in_progress:{ cls: "bg-amber-100 text-amber-800",    label: "IN PROGRESS" },
  completed:  { cls: "bg-emerald-100 text-emerald-800",label: "COMPLETED" },
  assigned:   { cls: "bg-purple-100 text-purple-800",  label: "ASSIGNED" },
};

export default function HandymanDashboard() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/jobs"); setJobs(Array.isArray(r.data) ? r.data : []); }
      catch { toast.error("Could not load jobs"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDropPhoto = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem("pending_handyman_photo", JSON.stringify({
          name: f.name, type: f.type, data: reader.result,
        }));
        nav("/admin/drafts");
        toast.success("Photo attached to Drafts");
      } catch { toast.error("Could not attach photo"); }
    };
    reader.readAsDataURL(f);
  };

  const nextStop = jobs.find(j => j.status === "en_route") || jobs.find(j => j.status === "scheduled") || jobs.find(j => j.status === "assigned");
  const stops = jobs.filter(j => j.id !== nextStop?.id).slice(0, 4);
  const funnel = {
    open:      jobs.filter(j => ["scheduled","assigned"].includes(j.status)).length,
    en_route:  jobs.filter(j => j.status === "en_route").length,
    in_prog:   jobs.filter(j => j.status === "in_progress").length,
    completed: jobs.filter(j => j.status === "completed").length,
  };
  const completedToday = jobs.filter(j => j.status === "completed").length;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="handyman-dashboard">
        {/* COMPACT AI CARD */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDropPhoto}
          onClick={() => nav("/admin/drafts")}
          data-testid="handyman-hero-snap"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-r from-purple-700 via-fuchsia-700 to-purple-900 p-4 sm:p-5 text-white hover:shadow-xl transition group flex items-center gap-4"
        >
          <div className="h-14 w-14 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
            <Sparkle weight="fill" size={26}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-purple-100/90">
              Claude · Drafts Queue
            </div>
            <div className="font-display font-black text-lg sm:text-xl leading-tight" data-testid="handyman-hero-title">
              Snap a fix. <span className="text-fuchsia-200">I'll draft the quote.</span>
            </div>
            <div className="text-[11px] text-purple-100/80 hidden sm:block">
              Drop a photo — sends it to your AI Drafts queue for a same-day estimate.
            </div>
          </div>
          <div className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white text-purple-900 font-black uppercase text-[10px] tracking-widest group-hover:scale-105 transition">
            <Camera weight="bold" size={13}/> Drop photo
          </div>
        </div>

        {/* EARNINGS + JOBS TODAY STRIP */}
        <div className="grid grid-cols-2 gap-2" data-testid="handyman-strip">
          <div className="rounded-xl bg-white border-2 border-slate-200 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
              <CheckCircle size={12}/> Completed today
            </div>
            <div className="font-display font-black text-2xl mt-0.5 text-emerald-700" data-testid="handyman-completed">
              {completedToday}
            </div>
          </div>
          <div className="rounded-xl bg-white border-2 border-slate-200 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
              <Hammer size={12}/> Jobs on the board
            </div>
            <div className="font-display font-black text-2xl mt-0.5 text-purple-800" data-testid="handyman-count">
              {jobs.length}
            </div>
          </div>
        </div>

        {/* NEXT STOP */}
        {nextStop && (
          <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="handyman-next-stop">
            <div className="px-4 py-2 bg-slate-900 text-white flex items-center gap-2">
              <MapPin weight="fill" size={14}/>
              <div className="text-[10px] font-black uppercase tracking-widest">Next stop</div>
              <StatusChip s={nextStop.status}/>
            </div>
            <div className="p-4">
              <div className="font-display font-black text-xl leading-tight" data-testid="next-stop-title">{nextStop.title}</div>
              <div className="text-sm text-slate-500 font-medium inline-flex items-center gap-1 mt-1">
                <House size={12}/> {nextStop.property_address}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`https://maps.google.com/?q=${encodeURIComponent(nextStop.property_address || "")}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="next-stop-nav"
                  className="h-10 px-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <MapPin weight="fill" size={13}/> Navigate
                </a>
                <button type="button" onClick={() => nav(`/admin/jobs?open=${nextStop.id}`)}
                  data-testid="next-stop-open"
                  className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Open job <ArrowRight size={12}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TODAY'S ROUTE */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="handyman-today-stops">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> Today's board
            </div>
            <button type="button" onClick={() => nav("/admin/jobs")}
              data-testid="handyman-all-jobs"
              className="text-[10px] font-black uppercase tracking-widest text-purple-600 hover:text-purple-800 inline-flex items-center gap-1">
              All jobs <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : stops.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="handyman-no-stops">
              Nothing else on the board. Grab a coffee.
            </div>
          ) : (
            stops.map((j, i) => (
              <div key={j.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`handyman-stop-${j.id}`}>
                <div className="h-9 w-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Hammer weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{j.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{j.property_address}</div>
                </div>
                <StatusChip s={j.status}/>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="handyman-funnel">
          <FunnelTile label="Open" value={funnel.open} icon={<Warning size={14}/>} accent="slate"/>
          <FunnelTile label="En route" value={funnel.en_route} icon={<PaperPlaneTilt size={14}/>} accent="cyan"/>
          <FunnelTile label="Working" value={funnel.in_prog} icon={<Hammer size={14}/>} accent="purple"/>
          <FunnelTile label="Completed" value={funnel.completed} icon={<CheckCircle size={14}/>} accent="emerald"/>
        </div>
      </div>
    </AdminShell>
  );
}

function StatusChip({ s }) {
  const m = STATUS_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
    purple:  "bg-purple-50 border-purple-200 text-purple-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
